import { d as colors } from "./focus-DaIphPOd.mjs";
import { c as transitions, h as fonts, l as radii, u as shadows } from "./motion-Cg-JnSSP.mjs";
import { r as useControllableState } from "./hooks-ChkgkdFK.mjs";
import React, { forwardRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region atoms/Button.tsx
/**
* EITELKEIT Button
* ═══════════════════════════════════════════════════════════════════
* The flagship component - toxic mint glow, clean interaction
*/
const styles$3 = {
	base: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: "8px",
		fontFamily: fonts.sans,
		fontWeight: 500,
		cursor: "pointer",
		transition: transitions.all,
		borderWidth: 0,
		borderStyle: "none",
		borderColor: "transparent",
		borderRadius: radii.sm,
		outline: "none",
		position: "relative",
		whiteSpace: "nowrap"
	},
	variants: {
		primary: {
			background: colors.mint,
			color: colors.void,
			fontWeight: 600
		},
		ghost: {
			background: "transparent",
			color: colors.textSecondary,
			borderWidth: 1,
			borderStyle: "solid",
			borderColor: colors.border
		},
		danger: {
			background: colors.dangerGlow,
			color: colors.danger,
			borderWidth: 1,
			borderStyle: "solid",
			borderColor: "rgba(255, 71, 87, 0.2)"
		},
		link: {
			background: "transparent",
			color: colors.mint,
			padding: "0"
		}
	},
	hoverVariants: {
		primary: {
			background: colors.mintDim,
			boxShadow: shadows.mintGlow
		},
		ghost: {
			background: colors.surface,
			color: colors.textPrimary,
			borderColor: colors.borderHover
		},
		danger: { background: "rgba(255, 71, 87, 0.25)" },
		link: { textDecoration: "underline" }
	},
	focusVariants: {
		primary: { boxShadow: shadows.focusRingStrong },
		ghost: { boxShadow: shadows.focusRingStrong },
		danger: { boxShadow: "0 0 0 2px #0a0a0b, 0 0 0 4px #ff4757" },
		link: {
			boxShadow: shadows.focusRing,
			borderRadius: "2px"
		}
	},
	sizes: {
		sm: {
			padding: "6px 12px",
			fontSize: "12px"
		},
		md: {
			padding: "8px 16px",
			fontSize: "13px"
		},
		lg: {
			padding: "12px 24px",
			fontSize: "14px"
		}
	},
	disabled: {
		opacity: .5,
		cursor: "not-allowed"
	},
	loading: { color: "transparent" }
};
const Button = forwardRef(({ variant, size, loading, disabled, leftIcon, rightIcon, children, style, onMouseEnter, onMouseLeave, onFocus, onBlur, ...props }, ref) => {
	const [isHovered, setIsHovered] = React.useState(false);
	const [isFocused, setIsFocused] = React.useState(false);
	const v = variant ?? "primary";
	const s = size ?? "md";
	const ld = loading ?? false;
	const handleMouseEnter = (e) => {
		setIsHovered(true);
		onMouseEnter?.(e);
	};
	const handleMouseLeave = (e) => {
		setIsHovered(false);
		onMouseLeave?.(e);
	};
	const handleFocus = (e) => {
		setIsFocused(true);
		onFocus?.(e);
	};
	const handleBlur = (e) => {
		setIsFocused(false);
		onBlur?.(e);
	};
	const computedStyle = {
		...styles$3.base,
		...styles$3.variants[v],
		...styles$3.sizes[s],
		...isHovered && !disabled ? styles$3.hoverVariants[v] : {},
		...isFocused && !disabled ? styles$3.focusVariants[v] : {},
		...disabled ? styles$3.disabled : {},
		...ld ? styles$3.loading : {},
		...style
	};
	return /* @__PURE__ */ jsxs("button", {
		ref,
		disabled: disabled || ld || void 0,
		style: computedStyle,
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
		onFocus: handleFocus,
		onBlur: handleBlur,
		...props,
		children: [
			ld && /* @__PURE__ */ jsx("span", {
				style: {
					position: "absolute",
					display: "flex",
					alignItems: "center",
					justifyContent: "center"
				},
				children: /* @__PURE__ */ jsx(Spinner, { size: s === "sm" ? 12 : s === "lg" ? 18 : 14 })
			}),
			leftIcon && /* @__PURE__ */ jsx("span", {
				style: { display: "flex" },
				children: leftIcon
			}),
			children,
			rightIcon && /* @__PURE__ */ jsx("span", {
				style: { display: "flex" },
				children: rightIcon
			})
		]
	});
});
Button.displayName = "Button";
function Spinner({ size }) {
	const sz = size ?? 14;
	return /* @__PURE__ */ jsxs("svg", {
		width: sz,
		height: sz,
		viewBox: "0 0 24 24",
		fill: "none",
		"aria-hidden": "true",
		style: { animation: "e-spin 1s linear infinite" },
		children: [/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-spin {
          to { transform: rotate(360deg); }
        }
      ` }), /* @__PURE__ */ jsx("circle", {
			cx: "12",
			cy: "12",
			r: "10",
			stroke: "currentColor",
			strokeWidth: "3",
			strokeLinecap: "round",
			strokeDasharray: "60 30"
		})]
	});
}
//#endregion
//#region atoms/IconButton.tsx
/**
* EITELKEIT IconButton
* ═══════════════════════════════════════════════════════════════════
* Compact icon-only button with mint-glow focus ring.
*/
const styles$2 = {
	base: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		transition: transitions.all,
		borderWidth: 0,
		borderStyle: "none",
		borderColor: "transparent",
		borderRadius: radii.sm,
		outline: "none",
		position: "relative",
		userSelect: "none"
	},
	variants: {
		primary: {
			background: colors.mint,
			color: colors.void,
			boxShadow: "none"
		},
		ghost: {
			background: "transparent",
			color: colors.textSecondary,
			borderWidth: 1,
			borderStyle: "solid",
			borderColor: colors.border
		},
		danger: {
			background: colors.dangerGlow,
			color: colors.danger,
			borderWidth: 1,
			borderStyle: "solid",
			borderColor: "rgba(255, 71, 87, 0.2)"
		}
	},
	hoverVariants: {
		primary: {
			background: colors.mintDim,
			boxShadow: shadows.mintGlow
		},
		ghost: {
			background: colors.surface,
			color: colors.textPrimary,
			borderColor: colors.borderHover
		},
		danger: { background: "rgba(255, 71, 87, 0.25)" }
	},
	focusVariants: {
		primary: { boxShadow: shadows.focusRingStrong },
		ghost: { boxShadow: shadows.focusRingStrong },
		danger: { boxShadow: "0 0 0 2px #0a0a0b, 0 0 0 4px #ff4757" }
	},
	sizes: {
		sm: {
			width: 28,
			height: 28,
			fontSize: 12
		},
		md: {
			width: 32,
			height: 32,
			fontSize: 13
		},
		lg: {
			width: 40,
			height: 40,
			fontSize: 14
		}
	},
	disabled: {
		opacity: .5,
		cursor: "not-allowed"
	},
	iconWrap: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		lineHeight: 1
	},
	glyph: {
		fontFamily: fonts.mono,
		letterSpacing: "0"
	}
};
const IconButton = forwardRef(({ variant, size, ariaLabel, isDisabled, glyph, icon, disabled, style, onMouseEnter, onMouseLeave, onFocus, onBlur, ...props }, ref) => {
	const [isHovered, setIsHovered] = React.useState(false);
	const [isFocused, setIsFocused] = React.useState(false);
	const v = variant ?? "ghost";
	const s = size ?? "md";
	const isActuallyDisabled = (isDisabled ?? false) || disabled === true;
	const handleMouseEnter = (e) => {
		setIsHovered(true);
		onMouseEnter?.(e);
	};
	const handleMouseLeave = (e) => {
		setIsHovered(false);
		onMouseLeave?.(e);
	};
	const handleFocus = (e) => {
		setIsFocused(true);
		onFocus?.(e);
	};
	const handleBlur = (e) => {
		setIsFocused(false);
		onBlur?.(e);
	};
	const { onClick: onNativeClick, ...passThroughProps } = props;
	const handleClick = (e) => {
		if (isActuallyDisabled) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		onNativeClick?.(e);
	};
	const computedStyle = {
		...styles$2.base,
		...styles$2.variants[v],
		...styles$2.sizes[s],
		...isHovered && !isActuallyDisabled ? styles$2.hoverVariants[v] : {},
		...isFocused && !isActuallyDisabled ? styles$2.focusVariants[v] : {},
		...isActuallyDisabled ? styles$2.disabled : {},
		...style
	};
	const content = icon ?? (glyph ? /* @__PURE__ */ jsx("span", {
		style: styles$2.glyph,
		children: glyph
	}) : null);
	return /* @__PURE__ */ jsx("button", {
		ref,
		type: props.type ?? "button",
		...ariaLabel === null ? {} : { "aria-label": ariaLabel },
		disabled: isActuallyDisabled,
		style: computedStyle,
		onClick: handleClick,
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
		onFocus: handleFocus,
		onBlur: handleBlur,
		...passThroughProps,
		children: /* @__PURE__ */ jsx("span", {
			style: styles$2.iconWrap,
			children: content
		})
	});
});
IconButton.displayName = "IconButton";
//#endregion
//#region atoms/Textarea.tsx
/**
* EITELKEIT Textarea
* ═══════════════════════════════════════════════════════════════════
*/
const Textarea = forwardRef(({ error, resize, disabled, style, onFocus, onBlur, ...props }, ref) => {
	const [isFocused, setIsFocused] = useState(false);
	const err = error ?? false;
	const r = resize ?? "vertical";
	const handleFocus = (e) => {
		setIsFocused(true);
		onFocus?.(e);
	};
	const handleBlur = (e) => {
		setIsFocused(false);
		onBlur?.(e);
	};
	return /* @__PURE__ */ jsx("textarea", {
		ref,
		disabled,
		style: {
			width: "100%",
			minHeight: "80px",
			padding: "12px 16px",
			background: colors.surface,
			border: `1px solid ${err ? colors.danger : isFocused ? colors.mint : colors.border}`,
			borderRadius: radii.md,
			color: colors.textPrimary,
			fontFamily: fonts.sans,
			fontSize: "14px",
			lineHeight: 1.5,
			outline: "none",
			resize: r,
			transition: transitions.all,
			boxShadow: isFocused && !err ? shadows.focusRing : err ? `0 0 0 3px ${colors.dangerGlow}` : "none",
			opacity: disabled ? .5 : 1,
			cursor: disabled ? "not-allowed" : "text",
			...style
		},
		onFocus: handleFocus,
		onBlur: handleBlur,
		...props
	});
});
Textarea.displayName = "Textarea";
//#endregion
//#region atoms/Select.tsx
/**
* EITELKEIT Select
* ═══════════════════════════════════════════════════════════════════
* Dropdown select with mint focus glow - matches Input styling
*/
const styles$1 = {
	container: {
		position: "relative",
		width: "100%"
	},
	select: {
		width: "100%",
		background: colors.surface,
		border: `1px solid ${colors.border}`,
		borderRadius: radii.md,
		color: colors.textPrimary,
		fontFamily: fonts.sans,
		outline: "none",
		transition: transitions.all,
		appearance: "none",
		cursor: "pointer",
		paddingRight: "36px"
	},
	selectFocus: {
		borderColor: colors.mint,
		boxShadow: shadows.focusRing
	},
	selectError: {
		borderColor: colors.danger,
		boxShadow: "0 0 0 3px " + colors.dangerGlow
	},
	sizes: {
		sm: {
			padding: "8px 12px",
			fontSize: "12px"
		},
		md: {
			padding: "12px 16px",
			fontSize: "14px"
		},
		lg: {
			padding: "14px 18px",
			fontSize: "16px"
		}
	},
	arrow: {
		position: "absolute",
		right: "12px",
		top: "50%",
		transform: "translateY(-50%)",
		pointerEvents: "none",
		color: colors.textMuted,
		width: "16px",
		height: "16px"
	},
	disabled: {
		opacity: .5,
		cursor: "not-allowed"
	}
};
const Select = forwardRef(({ options, size, error, disabled, style, onFocus, onBlur, ...props }, ref) => {
	const [isFocused, setIsFocused] = React.useState(false);
	const s = size ?? "md";
	const err = error ?? false;
	const handleFocus = (e) => {
		setIsFocused(true);
		onFocus?.(e);
	};
	const handleBlur = (e) => {
		setIsFocused(false);
		onBlur?.(e);
	};
	const selectStyle = {
		...styles$1.select,
		...styles$1.sizes[s],
		...isFocused && !err ? styles$1.selectFocus : {},
		...err ? styles$1.selectError : {},
		...disabled ? styles$1.disabled : {},
		...style
	};
	const normalizedOptions = options.map((opt) => typeof opt === "string" ? {
		label: opt,
		value: opt,
		disabled: null
	} : opt);
	return /* @__PURE__ */ jsxs("div", {
		style: styles$1.container,
		children: [/* @__PURE__ */ jsx("select", {
			ref,
			disabled,
			style: selectStyle,
			onFocus: handleFocus,
			onBlur: handleBlur,
			...props,
			children: normalizedOptions.map((option) => /* @__PURE__ */ jsx("option", {
				value: option.value,
				disabled: option.disabled ?? void 0,
				children: option.label
			}, option.value))
		}), /* @__PURE__ */ jsx("svg", {
			style: styles$1.arrow,
			viewBox: "0 0 16 16",
			fill: "none",
			children: /* @__PURE__ */ jsx("path", {
				d: "M4 6L8 10L12 6",
				stroke: "currentColor",
				strokeWidth: "2",
				strokeLinecap: "round",
				strokeLinejoin: "round"
			})
		})]
	});
});
Select.displayName = "Select";
//#endregion
//#region atoms/Badge.tsx
const styles = {
	base: {
		display: "inline-flex",
		alignItems: "center",
		fontFamily: fonts.sans,
		fontWeight: 600,
		borderRadius: "4px"
	},
	variants: {
		default: {
			background: colors.surface,
			color: colors.textSecondary
		},
		mint: {
			background: "rgba(57, 255, 159, 0.2)",
			color: "#2dd882",
			border: "1px solid rgba(57, 255, 159, 0.35)"
		},
		mod: {
			background: "rgba(57, 255, 159, 0.2)",
			color: "#2dd882",
			border: "1px solid rgba(57, 255, 159, 0.35)"
		},
		sub: {
			background: "rgba(168, 85, 247, 0.2)",
			color: "#c084fc",
			border: "1px solid rgba(168, 85, 247, 0.35)"
		},
		vip: {
			background: "rgba(255, 184, 57, 0.2)",
			color: "#fbbf24",
			border: "1px solid rgba(255, 184, 57, 0.35)"
		},
		danger: {
			background: "rgba(255, 71, 87, 0.2)",
			color: "#ff6b7a",
			border: "1px solid rgba(255, 71, 87, 0.35)"
		},
		warning: {
			background: "rgba(255, 184, 57, 0.2)",
			color: "#fbbf24",
			border: "1px solid rgba(255, 184, 57, 0.35)"
		},
		info: {
			background: "rgba(57, 192, 255, 0.2)",
			color: "#5cc8ff",
			border: "1px solid rgba(57, 192, 255, 0.35)"
		},
		success: {
			background: "rgba(57, 255, 159, 0.2)",
			color: "#2dd882",
			border: "1px solid rgba(57, 255, 159, 0.35)"
		},
		muted: {
			background: colors.voidLight,
			color: colors.textMuted
		}
	},
	sizes: {
		sm: {
			padding: "2px 6px",
			fontSize: "10px"
		},
		md: {
			padding: "4px 8px",
			fontSize: "11px"
		}
	}
};
function Badge({ variant, size, style, children, ...props }) {
	const v = variant ?? "mint";
	const s = size ?? "sm";
	return /* @__PURE__ */ jsx("span", {
		style: {
			...styles.base,
			...styles.variants[v],
			...styles.sizes[s],
			...style
		},
		...props,
		children
	});
}
//#endregion
//#region atoms/Switch.tsx
/**
* EITELKEIT Switch
* ═══════════════════════════════════════════════════════════════════
* Toggle switch with mint glow when active
*/
const sizes = {
	sm: {
		track: {
			width: 32,
			height: 18
		},
		thumb: 14,
		offset: 2
	},
	md: {
		track: {
			width: 40,
			height: 22
		},
		thumb: 18,
		offset: 2
	},
	lg: {
		track: {
			width: 48,
			height: 26
		},
		thumb: 22,
		offset: 2
	}
};
const Switch = forwardRef(({ checked: controlledChecked, defaultChecked, onChange, size, label, disabled, style, "aria-label": ariaLabel, ...props }, ref) => {
	const sz = size ?? "md";
	const [checked, setChecked] = useControllableState(controlledChecked, defaultChecked ?? false, onChange ?? void 0);
	const [isFocused, setIsFocused] = useState(false);
	const s = sizes[sz];
	const trackStyle = {
		position: "relative",
		width: s.track.width,
		height: s.track.height,
		borderRadius: s.track.height,
		background: checked ? colors.mint : colors.surface,
		border: `1px solid ${checked ? colors.mint : colors.border}`,
		cursor: disabled ? "not-allowed" : "pointer",
		transition: transitions.all,
		opacity: disabled ? .5 : 1,
		boxShadow: isFocused && !disabled ? shadows.focusRingStrong : checked ? shadows.mintGlow : "none"
	};
	const thumbStyle = {
		position: "absolute",
		top: s.offset,
		left: checked ? s.track.width - s.thumb - s.offset - 2 : s.offset,
		width: s.thumb,
		height: s.thumb,
		borderRadius: "50%",
		background: checked ? colors.void : colors.textSecondary,
		transition: transitions.all
	};
	return /* @__PURE__ */ jsxs("label", {
		style: {
			display: "inline-flex",
			alignItems: "center",
			gap: label ? "12px" : 0,
			cursor: disabled ? "not-allowed" : "pointer",
			...style
		},
		children: [
			/* @__PURE__ */ jsx("input", {
				ref,
				type: "checkbox",
				role: "switch",
				checked,
				onChange: (e) => setChecked(e.target.checked),
				onFocus: () => setIsFocused(true),
				onBlur: () => setIsFocused(false),
				disabled,
				"aria-checked": checked,
				"aria-label": ariaLabel || (typeof label === "string" ? label : void 0),
				style: {
					position: "absolute",
					opacity: 0,
					width: 0,
					height: 0,
					pointerEvents: "none"
				},
				...props
			}),
			/* @__PURE__ */ jsx("span", {
				style: trackStyle,
				"aria-hidden": "true",
				children: /* @__PURE__ */ jsx("span", { style: thumbStyle })
			}),
			label && /* @__PURE__ */ jsx("span", {
				style: {
					fontSize: sz === "sm" ? "12px" : sz === "lg" ? "16px" : "14px",
					color: colors.textSecondary
				},
				children: label
			})
		]
	});
});
Switch.displayName = "Switch";
//#endregion
export { IconButton as a, Textarea as i, Badge as n, Button as o, Select as r, Switch as t };

//# sourceMappingURL=Switch-I-T1TqTa.mjs.map